import React from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'root/apps/helpers/react-renderer';

import { StyledBodyText, StyledListText, StyledModal, StyledOrderList } from './Modal.style';

interface ArchiveModalBypassProp {
  callback?: (result: boolean) => void;
  unmount?: () => void;
}

const ArchiveModalBypass: React.FC<ArchiveModalBypassProp> = ({ callback, unmount }) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [hasQOH, setHasQOH] = React.useState(false);
  const [hasInventoryMovement, setHasInventoryMovement] = React.useState(false);
  const [hasNegativeInventory, setHasNegativeInventory] = React.useState(false);
  const [inventoryUri, setInventoryUri] = React.useState('');
  const [itemName, setItemName] = React.useState('');
  const [inventoryMovementType, setInventoryMovementType] = React.useState('');

  const { id: itemId } = window.history.getFilters();

  const decodeHtmlEntities = (text: string): string =>
    new DOMParser().parseFromString(text, 'text/html').body?.textContent || '';

  const handleArchiveitem = React.useCallback(() => {
    setIsOpen(false);
    if (callback) callback(true);
    if (unmount) unmount();
  }, [callback, unmount, setIsOpen]);

  const handleDismiss = () => {
    setIsOpen(false);
    if (callback) callback(false);
    if (unmount) unmount();
  };

  const handleRedirect = () => {
    window.merchantos.utils.openUrl(inventoryUri);
  };

  const getArchivePrecheck = React.useCallback(async () => {
    try {
      if (!itemId) {
        setIsError(true);
        setIsLoading(false);
        return;
      }

      const parsedItemId = Number(itemId);

      const res = await fetch(`/admin/items/${parsedItemId}/archive-precheck`);
      const data = await res.json();

      const hasPendingInventoryMovement = data?.pendingInventoryMovements?.length > 0;
      setHasNegativeInventory(data?.hasNegativeInventory);

      setHasQOH(data?.hasQoh);
      setItemName(decodeHtmlEntities(data?.description));

      if (hasPendingInventoryMovement) {
        setHasInventoryMovement(hasPendingInventoryMovement);
        setInventoryUri(data?.pendingInventoryMovements[0].uri);
        setInventoryMovementType(data?.pendingInventoryMovements[0].type);
      }
    } catch (err) {
      setIsError(true);
    }
    setIsLoading(false);
  }, [itemId]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isOpen) {
        e.preventDefault();
        handleArchiveitem();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleArchiveitem]);

  React.useEffect(() => {
    getArchivePrecheck();
  }, [getArchivePrecheck]);

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleDismiss}>
      {isLoading ? (
        <>
          <ModalHeader data-test="archive-modal-loading">
            <Bone height={3} width="50%" />
          </ModalHeader>
          <ModalBody style={{ paddingTop: '12px', paddingBottom: '12px' }}>
            <Bone height={3} style={{ marginBottom: '20px' }} />
            <Bone height={10} style={{ marginBottom: '4px' }} />
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="right">
              <Bone height={5} width="30%" />
            </Flex>
          </ModalFooter>
        </>
      ) : null}
      {!isLoading ? (
        <>
          <ModalHeader data-test="item-archive-modal-header">
            <Text>
              Do you want to archive <span className="notranslate">{itemName}</span>?
            </Text>
          </ModalHeader>
          <ModalBody>
            {(!hasQOH && !hasInventoryMovement && !hasNegativeInventory) || isError ? (
              <>
                <Text mb={2}>The item will remain in your inventory as an archived item.</Text>
                <Text data-test="item-archive-modal-normal-archive-msg">
                  You will need to un-archive <span className="notranslate">{itemName}</span> before
                  using it again.
                </Text>
              </>
            ) : (
              <>
                <StyledBodyText>
                  Archiving this item will have the following impacts on your inventory:
                </StyledBodyText>
                <StyledOrderList>
                  {hasQOH ? (
                    <StyledListText data-test="item-archive-modal-qoh-warning">
                      This item has inventory in one or more shops. Archiving it will reset all
                      inventory in all shops to zero. This action cannot be undone.
                    </StyledListText>
                  ) : null}
                  {hasInventoryMovement ? (
                    <StyledListText data-test="item-archive-modal-inventory-movement-warning">
                      This item is currently part of one or more pending inventory workflows such as
                      this{' '}
                      <TextLink
                        data-test="item-archive-modal-uri-redirect"
                        fontWeight={600}
                        onClick={handleRedirect}
                      >
                        {inventoryMovementType}
                      </TextLink>
                      . Archiving it will block these workflows from proceeding until the item is
                      either restored or removed from the workflow.
                    </StyledListText>
                  ) : null}
                  {hasNegativeInventory ? (
                    <StyledListText data-test="item-archive-modal-negative-inventory-warning">
                      This item has outstanding negative inventory, which will be deleted by
                      archiving the item. You will no longer be able to perform referenced refunds
                      of units which caused negative inventory. Resolving outstanding negative
                      inventory is recommended before archiving the item.
                    </StyledListText>
                  ) : null}
                </StyledOrderList>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="end">
              <Button onClick={handleDismiss}>Cancel</Button>
              <Button
                fill
                variant="danger"
                ml={2}
                onClick={handleArchiveitem}
                data-test="item-archive-modal-archive-btn"
              >
                Archive
              </Button>
            </Flex>
          </ModalFooter>
        </>
      ) : null}
    </StyledModal>
  );
};

export { ArchiveModalBypass };

interface SetArchiveModalBypassProps {
  callback?: ArchiveModalBypassProp['callback'];
}

export const setArchiveModalBypass = (setArchiveModalBypassProps: SetArchiveModalBypassProps) => {
  const RenderedComponent = createReactRenderer({
    component: ArchiveModalBypass,
    selector: '[data-react-root="ReactModal"]',
  });
  const props = { ...setArchiveModalBypassProps, unmount: RenderedComponent.unmount };
  RenderedComponent.mount({ props });
};
